import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle, Clock, FileText, Zap, Phone, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../lib/api';
import { toast } from 'sonner';

const STATUS_LABELS = {
  estimate: 'Estimate Pending Approval',
  approved: 'Approved - Scheduling',
  in_progress: 'Work In Progress',
  completed: 'Work Completed',
  invoiced: 'Invoice Sent',
  paid: 'Paid - Complete'
};

const STATUS_STEPS = ['estimate', 'approved', 'in_progress', 'completed', 'invoiced', 'paid'];

const fmt = (n) => `$${(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CustomerPortal = () => {
  const { jobId } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadJob();
  }, [jobId]);

  const loadJob = async () => {
    try {
      const res = await api.portal.getJob(jobId);
      setJob(res.data);
    } catch (e) {
      setError('Job not found or link has expired.');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    setApproving(true);
    try {
      await api.portal.approveJob(jobId);
      toast.success('Estimate approved! We will be in touch shortly.');
      loadJob();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to approve');
    } finally {
      setApproving(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-primary-dark flex items-center justify-center">
      <div className="text-electric-teal animate-pulse text-lg">Loading...</div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-primary-dark flex items-center justify-center px-4">
      <div className="text-center">
        <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-white mb-2">Not Found</h1>
        <p className="text-white/50">{error}</p>
      </div>
    </div>
  );

  const currentStep = STATUS_STEPS.indexOf(job.job_status);
  const balance = (job.final_bill_amount || 0) - (job.amount_paid || 0);

  return (
    <div className="min-h-screen bg-primary-dark" data-testid="customer-portal">
      {/* Header */}
      <div className="bg-secondary-dark border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 py-6 flex items-center justify-between">
          <div>
            <h1 className="text-electric-teal font-black text-xl tracking-tight">ISLAND COASTAL ELECTRIC</h1>
            <p className="text-white/40 text-sm">Customer Portal</p>
          </div>
          <a href="tel:7789602377" className="flex items-center gap-2 text-white/60 hover:text-electric-teal text-sm transition-colors">
            <Phone className="w-4 h-4" /> 778.960.2377
          </a>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8 space-y-8">
        {/* Job Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-3xl font-bold text-white">{job.job_number}</h2>
            <p className="text-white/50 mt-1">{job.service_type} - {job.address}</p>
          </div>
          <div className="text-right">
            <span className="bg-electric-teal/20 text-electric-teal px-4 py-2 rounded-full text-sm font-bold uppercase">
              {STATUS_LABELS[job.job_status] || job.job_status}
            </span>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="bg-secondary-dark rounded-xl p-6 border border-white/10">
          <h3 className="text-white font-semibold mb-4 text-sm">Job Progress</h3>
          <div className="flex items-center justify-between relative">
            <div className="absolute top-4 left-0 right-0 h-0.5 bg-white/10" />
            <div className="absolute top-4 left-0 h-0.5 bg-electric-teal transition-all" 
              style={{ width: `${Math.max(0, (currentStep / (STATUS_STEPS.length - 1)) * 100)}%` }} />
            {STATUS_STEPS.map((step, i) => (
              <div key={step} className="relative flex flex-col items-center z-10">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                  i <= currentStep ? 'bg-electric-teal text-primary-dark' : 'bg-white/10 text-white/30'
                }`}>
                  {i < currentStep ? <CheckCircle className="w-4 h-4" /> : i + 1}
                </div>
                <span className={`text-[10px] mt-2 font-medium capitalize hidden sm:block ${
                  i <= currentStep ? 'text-electric-teal' : 'text-white/30'
                }`}>{step.replace('_', ' ')}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Dates */}
        {(job.estimated_start_date || job.actual_start_date || job.end_date) && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {job.estimated_start_date && (
              <div className="bg-secondary-dark rounded-lg p-4 border border-white/10">
                <p className="text-white/40 text-xs mb-1">Estimated Start</p>
                <p className="text-white font-semibold">{job.estimated_start_date}</p>
              </div>
            )}
            {job.actual_start_date && (
              <div className="bg-secondary-dark rounded-lg p-4 border border-white/10">
                <p className="text-white/40 text-xs mb-1">Actual Start</p>
                <p className="text-white font-semibold">{job.actual_start_date}</p>
              </div>
            )}
            {job.end_date && (
              <div className="bg-secondary-dark rounded-lg p-4 border border-white/10">
                <p className="text-white/40 text-xs mb-1">Completed</p>
                <p className="text-white font-semibold">{job.end_date}</p>
              </div>
            )}
          </div>
        )}

        {/* Line Items */}
        <div className="bg-secondary-dark rounded-xl border border-white/10 overflow-hidden">
          <div className="p-5 border-b border-white/10">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <FileText className="w-4 h-4 text-electric-teal" /> Estimate Details
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left text-white/40 text-xs font-medium p-4">Description</th>
                  <th className="text-center text-white/40 text-xs font-medium p-4 w-20">Qty</th>
                  <th className="text-right text-white/40 text-xs font-medium p-4 w-24">Rate</th>
                  <th className="text-right text-white/40 text-xs font-medium p-4 w-24">Amount</th>
                </tr>
              </thead>
              <tbody>
                {(job.line_items || []).map((item, i) => (
                  <tr key={i} className="border-b border-white/5">
                    <td className="p-4 text-white text-sm">{item.description}</td>
                    <td className="p-4 text-white/60 text-sm text-center">{item.quantity}</td>
                    <td className="p-4 text-white/60 text-sm text-right">{fmt(item.rate)}</td>
                    <td className="p-4 text-white font-medium text-sm text-right">{fmt(item.amount)}</td>
                  </tr>
                ))}
                {(job.add_on_items || []).length > 0 && (
                  <>
                    <tr><td colSpan={4} className="p-3 text-electric-teal text-xs font-bold uppercase">Add-on Items</td></tr>
                    {job.add_on_items.map((item, i) => (
                      <tr key={`addon-${i}`} className="border-b border-white/5">
                        <td className="p-4 text-white text-sm">{item.description}</td>
                        <td className="p-4 text-white/60 text-sm text-center">{item.quantity}</td>
                        <td className="p-4 text-white/60 text-sm text-right">{fmt(item.rate)}</td>
                        <td className="p-4 text-white font-medium text-sm text-right">{fmt(item.amount)}</td>
                      </tr>
                    ))}
                  </>
                )}
              </tbody>
            </table>
          </div>
          {/* Totals */}
          <div className="p-5 border-t border-white/10 space-y-2">
            <div className="flex justify-between text-sm"><span className="text-white/40">Subtotal</span><span className="text-white">{fmt(job.subtotal)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-white/40">Tax ({job.tax_rate}%)</span><span className="text-white">{fmt(job.tax_amount)}</span></div>
            {job.add_on_total > 0 && <div className="flex justify-between text-sm"><span className="text-white/40">Add-ons</span><span className="text-white">{fmt(job.add_on_total)}</span></div>}
            <div className="flex justify-between text-lg font-bold pt-2 border-t border-white/10">
              <span className="text-white">Total</span>
              <span className="text-electric-teal">{fmt(job.final_bill_amount || job.estimate_total)}</span>
            </div>
            {job.amount_paid > 0 && (
              <>
                <div className="flex justify-between text-sm"><span className="text-green-400">Paid</span><span className="text-green-400">{fmt(job.amount_paid)}</span></div>
                {balance > 0 && <div className="flex justify-between text-lg font-bold"><span className="text-red-400">Balance Due</span><span className="text-red-400">{fmt(balance)}</span></div>}
              </>
            )}
          </div>
        </div>

        {/* Notes */}
        {job.notes && (
          <div className="bg-secondary-dark rounded-xl p-5 border border-white/10">
            <h3 className="text-white font-semibold text-sm mb-2">Notes</h3>
            <p className="text-white/60 text-sm whitespace-pre-wrap">{job.notes}</p>
          </div>
        )}

        {/* Approve Button */}
        {job.job_status === 'estimate' && (
          <div className="bg-electric-teal/10 rounded-xl p-6 border border-electric-teal/30 text-center">
            <h3 className="text-white font-bold text-lg mb-2">Ready to proceed?</h3>
            <p className="text-white/60 text-sm mb-5">Approve this estimate and we'll contact you to schedule your project.</p>
            <Button
              onClick={handleApprove}
              disabled={approving}
              className="bg-electric-teal text-primary-dark font-bold px-8 py-3 text-lg"
              data-testid="approve-estimate-btn"
            >
              <CheckCircle className="w-5 h-5 mr-2" />
              {approving ? 'Approving...' : 'Approve Estimate'}
            </Button>
          </div>
        )}

        {/* Footer */}
        <div className="text-center py-6 border-t border-white/10">
          <p className="text-white/30 text-xs">Island Coastal Electric | BC Hydro Alliance Member</p>
          <p className="text-white/30 text-xs">2668 Sooke Road, Victoria, BC V9B 1Y6 | 778.960.2377</p>
        </div>
      </div>
    </div>
  );
};

export default CustomerPortal;
